/**
 * 将 API 返回的 componentList 转换为 PANEL_SIZE_PRESETS 格式。
 *
 * componentType → DRAW_TYPE 映射：
 *   HOLE → keyHole, INNER_HOLE → perforation, STAND → liPai, STAND_HOLE → baseHole
 */

import { DRAW_TYPE, DEFAULT_CONFIG } from './constants.js';

/** componentType → DRAW_TYPE 映射表 */
var COMPONENT_TYPE_MAP = {
  HOLE: DRAW_TYPE.KEY_HOLE,
  INNER_HOLE: DRAW_TYPE.PERFORATION,
  STAND: DRAW_TYPE.LI_PAI,
  STAND_HOLE: DRAW_TYPE.BASE_HOLE
};

/**
 * 解析 attr JSON 字符串为 geometryConfig。
 * @param {string} componentType - API 的 componentType
 * @param {string} attrJson - JSON 字符串
 * @returns {Object} geometryConfig
 */
function parseAttrToGeometry(componentType, attrJson) {
  var attr = {};
  try {
    attr = JSON.parse(attrJson) || {};
  } catch (e) {
    attr = {};
  }

  switch (componentType) {
    case 'HOLE':
      return {
        outerHoleDiameterMm: parseFloat(attr.outer_diameter) || 0,
        innerHoleDiameterMm: parseFloat(attr.inner_diameter) || 0
      };
    case 'INNER_HOLE':
      return {
        radiusMm: parseFloat(attr.radius) || 0
      };
    case 'STAND':
      return {
        slotWidthMm: parseFloat(attr.width) || 0,
        slotHeightMm: parseFloat(attr.height) || 0,
        slotRange:
          parseFloat(attr.slotRange) ||
          DEFAULT_CONFIG.liPaiSlotRangePercent / 100
      };
    case 'STAND_HOLE':
      // radius 字段暂不转换，预留
      return {
        widthMm: parseFloat(attr.width) || 0,
        heightMm: parseFloat(attr.height) || 0
      };
    default:
      return {};
  }
}

/**
 * 根据 componentType 和 attr 生成唯一 id。
 * @param {string} componentType
 * @param {Object} attr - 已解析的 attr 对象
 * @returns {string}
 */
function generatePresetId(componentType, attr) {
  switch (componentType) {
    case 'HOLE':
      return 'kh-' + attr.inner_diameter + '-' + attr.outer_diameter;
    case 'INNER_HOLE':
      return 'pf-' + attr.radius;
    case 'STAND':
      return 'lp-' + attr.width + '-' + attr.height;
    case 'STAND_HOLE':
      return 'bh-' + attr.width + '-' + attr.height;
    default:
      return 'unknown';
  }
}

/**
 * 将 API 返回的 componentList 转换为页面可用的预设数据。
 *
 * @param {Array} componentList - skuTemplate.componentList
 * @returns {{
 *   presets: Object.<string, Array>,
 *   maxCount: Object.<string, number>,
 *   minCount: Object.<string, number>
 * }}
 */
export function buildPresetsFromApi(componentList) {
  var presets = {};
  var maxCount = {};
  var minCount = {};

  if (!componentList || !componentList.length) {
    return { presets: presets, maxCount: maxCount, minCount: minCount };
  }

  var i, j, group, drawType, item, attr, list;
  for (i = 0; i < componentList.length; i++) {
    group = componentList[i];
    drawType = COMPONENT_TYPE_MAP[group.componentType];
    if (!drawType) continue;

    maxCount[drawType] = group.maxQty;
    minCount[drawType] = group.minQty;

    list = [];
    if (group.list && group.list.length) {
      for (j = 0; j < group.list.length; j++) {
        item = group.list[j];
        attr = {};
        try {
          attr = JSON.parse(item.attr) || {};
        } catch (e) {
          attr = {};
        }

        list.push({
          id: generatePresetId(group.componentType, attr),
          label: item.componentName || '',
          isDefault: j === 0,
          thumbSrc: item.icon || '',
          helpUrl: item.helpUrl || '',
          geometryConfig: parseAttrToGeometry(group.componentType, item.attr)
        });
      }
    }
    presets[drawType] = list;
  }
  return { presets: presets, maxCount: maxCount, minCount: minCount };
}

/**
 * 判断值是否为有效数字。
 * @param {*} value - 待判断的值
 * @returns {boolean} 是否为有效数字
 */
function isValidNumber(value) {
  return typeof value === 'number' && isFinite(value);
}

/**
 * 判断值是否为有效正数。
 * @param {*} value - 待判断的值
 * @returns {boolean} 是否为有效正数
 */
function isPositiveNumber(value) {
  return isValidNumber(value) && value > 0;
}

/**
 * 判断数组中的指定数字字段是否都有效。
 * @param {Array} list - 待检查数组
 * @param {Array<number>} indexes - 需要检查的下标
 * @returns {boolean} 指定字段是否都是有效数字
 */
function hasValidNumberFields(list, indexes) {
  // 数据结构不完整时不能还原组件位置。
  if (!Array.isArray(list)) {
    return false;
  }
  return indexes.every(function (index) {
    return isValidNumber(list[index]);
  });
}

/**
 * 判断数组中的指定字段是否都为正数。
 * @param {Array} list - 待检查数组
 * @param {Array<number>} indexes - 需要检查的下标
 * @returns {boolean} 指定字段是否都是正数
 */
function hasPositiveNumberFields(list, indexes) {
  // 数据结构不完整时不能还原组件尺寸。
  if (!Array.isArray(list)) {
    return false;
  }
  return indexes.every(function (index) {
    return isPositiveNumber(list[index]);
  });
}

/**
 * 生成从 acrylic 反向还原 componentData 时使用的组件 id。
 * @param {string} type - 组件类型
 * @param {number} index - 组件序号
 * @returns {string} 组件 id
 */
function createAcrylicComponentId(type, index) {
  return 'acrylic_' + type + '_' + index;
}

/**
 * 根据画布信息获取还原组件用的基准尺寸。
 * @param {Object} canvas - layerData.canvas
 * @returns {{width:number,height:number}} 基准宽高
 */
function getAcrylicRestoreCanvasSize(canvas) {
  var displayWidth = canvas && Number(canvas.displayWidth);
  var displayHeight = canvas && Number(canvas.displayHeight);
  var width = displayWidth > 0 ? displayWidth : 0;
  var height = displayHeight > 0 ? displayHeight : width;
  return {
    width: width,
    height: height || width
  };
}

/**
 * 判断 componentData 是否包含可还原的组件数据。
 * @param {Object} componentData - 多组件数据
 * @returns {boolean} 是否包含组件数据
 */
function hasComponentData(componentData) {
  // 没有对象时无需继续判断。
  if (!componentData || typeof componentData !== 'object') {
    return false;
  }
  return Object.keys(componentData).some(function (key) {
    return Array.isArray(componentData[key]) && componentData[key].length > 0;
  });
}

/**
 * 将后端 acrylic 数据反向还原为编辑页 componentData。
 * 坐标按后端比例写入展示列表，并按当前 canvas 尺寸补 origin*List，供组件服务恢复状态。
 *
 * @param {Object} acrylic - 后端 acrylic 数据
 * @param {Object} [options] - 还原配置
 * @param {Object} [options.canvas] - layerData.canvas，用于把比例坐标映射为画布坐标
 * @returns {Object} layerData.componentData 格式数据
 */
export function convertAcrylicToComponentData(acrylic, options) {
  // 后端没有 acrylic 时不生成组件状态。
  if (!acrylic || typeof acrylic !== 'object') {
    return {};
  }

  var canvasSize = getAcrylicRestoreCanvasSize((options || {}).canvas);
  var baseCanvasWidth = canvasSize.width;
  var baseCanvasHeight = canvasSize.height;
  var componentData = {};

  // 钥匙扣孔位使用 edgeHoles: [[x, y, innerR, outerR], ...]。
  if (Array.isArray(acrylic.edgeHoles) && acrylic.edgeHoles.length) {
    componentData.keyPointList = [];
    componentData.originCenterPointList = [];
    acrylic.edgeHoles.forEach(function (hole, index) {
      // 坐标与半径完整时才恢复该钥匙扣组件。
      if (
        !hasValidNumberFields(hole, [0, 1]) ||
        !hasPositiveNumberFields(hole, [2, 3])
      ) {
        return;
      }
      var componentId = createAcrylicComponentId(DRAW_TYPE.KEY_HOLE, index);
      var point = [hole[0], hole[1]];
      componentData.keyPointList.push(point);
      componentData.originCenterPointList.push({
        x: hole[0] * baseCanvasWidth,
        y: hole[1] * baseCanvasHeight,
        baseCanvasWidth: baseCanvasWidth,
        canvasRoundWhiteSpace: 0,
        componentId: componentId,
        geometryConfig: {
          innerHoleDiameterMm: hole[2] * 2,
          outerHoleDiameterMm: hole[3] * 2
        },
        sizePresetId: '',
        enableMagnifier: true
      });
    });
  }

  // 立牌插口使用 slotRect: [x, y, widthRatio, heightRatio, slotWidthMm, slotHeightMm]。
  if (
    hasValidNumberFields(acrylic.slotRect, [0, 1]) &&
    hasPositiveNumberFields(acrylic.slotRect, [2, 3])
  ) {
    var slotRect = acrylic.slotRect;
    var hasPhysicalSize = hasPositiveNumberFields(slotRect, [4, 5]);
    var slotWidthMm = hasPhysicalSize ? slotRect[4] : slotRect[2];
    var slotHeightMm = hasPhysicalSize ? slotRect[5] : slotRect[3];
    var liPaiComponentId = createAcrylicComponentId(DRAW_TYPE.LI_PAI, 0);
    var socketInfo = {
      slotRect: slotRect.slice(),
      componentId: liPaiComponentId
    };
    var originSocketInfo = {
      slotRect: slotRect.slice(),
      baseCanvasWidth: baseCanvasWidth,
      canvasRoundWhiteSpace: 0,
      componentId: liPaiComponentId,
      geometryConfig: {
        slotWidthMm: slotWidthMm,
        slotHeightMm: slotHeightMm
      },
      sizePresetId: '',
      enableMagnifier: true
    };
    componentData.socketInfoList = [socketInfo];
    componentData.originSocketInfoList = [originSocketInfo];
  }

  // 穿孔使用 innerHoles: [[x, y, r], ...]。
  if (Array.isArray(acrylic.innerHoles) && acrylic.innerHoles.length) {
    componentData.perforationPointList = [];
    componentData.originPerforationPointList = [];
    acrylic.innerHoles.forEach(function (hole, index) {
      // 坐标与半径完整时才恢复该穿孔组件。
      if (
        !hasValidNumberFields(hole, [0, 1]) ||
        !hasPositiveNumberFields(hole, [2])
      ) {
        return;
      }
      var componentId = createAcrylicComponentId(DRAW_TYPE.PERFORATION, index);
      var point = [hole[0], hole[1]];
      componentData.perforationPointList.push(point);
      componentData.originPerforationPointList.push({
        x: hole[0] * baseCanvasWidth,
        y: hole[1] * baseCanvasHeight,
        baseCanvasWidth: baseCanvasWidth,
        canvasRoundWhiteSpace: 0,
        componentId: componentId,
        geometryConfig: {
          radiusMm: hole[2]
        },
        sizePresetId: '',
        enableMagnifier: true
      });
    });
  }

  // 底座孔使用 slotHoles: [[x, y, angle, slotWidthMm, slotHeightMm], ...]。
  if (Array.isArray(acrylic.slotHoles) && acrylic.slotHoles.length) {
    componentData.baseHoleInfoList = [];
    componentData.originBaseHoleInfoList = [];
    acrylic.slotHoles.forEach(function (hole, index) {
      // 坐标、角度与尺寸完整时才恢复该底座孔组件。
      if (
        !hasValidNumberFields(hole, [0, 1, 2]) ||
        !hasPositiveNumberFields(hole, [3, 4])
      ) {
        return;
      }
      var componentId = createAcrylicComponentId(DRAW_TYPE.BASE_HOLE, index);
      var centerPoint = {
        x: hole[0],
        y: hole[1]
      };
      componentData.baseHoleInfoList.push({
        centerPoint: centerPoint,
        width: hole[3],
        height: hole[4],
        rotationRad: hole[2],
        componentId: componentId
      });
      componentData.originBaseHoleInfoList.push({
        centerPoint: {
          x: hole[0] * baseCanvasWidth,
          y: hole[1] * baseCanvasHeight
        },
        width: hole[3],
        height: hole[4],
        rotationRad: hole[2],
        baseCanvasWidth: baseCanvasWidth,
        canvasRoundWhiteSpace: 0,
        componentId: componentId,
        geometryConfig: {
          widthMm: hole[3],
          heightMm: hole[4]
        },
        sizePresetId: '',
        enableMagnifier: true
      });
    });
  }

  // 过滤掉只有空数组的还原结果，避免误判存在组件数据。
  if (!hasComponentData(componentData)) {
    return {};
  }
  return componentData;
}

/**
 * 将自定义组件导出的 componentData 转换为 acrylic 数据格式。
 * 用于提交时将多部件数据转为后端接口需要的 acrylic 字段。
 *
 * @param {Object} componentData - layerData.componentData
 * @param {Array<Array<number>>} componentData.keyPointList - 钥匙扣比例坐标 [[x, y], ...]
 * @param {Array<Object>} componentData.originCenterPointList - 钥匙扣元数据（含 geometryConfig）
 * @param {Array<Object>} componentData.socketInfoList - 立牌信息（含 slotRect 坐标比例）
 * @param {Array<Object>} componentData.originSocketInfoList - 立牌元数据（含 geometryConfig.slotWidthMm/slotHeightMm）
 * @param {Array<Array<number>>} componentData.perforationPointList - 穿孔比例坐标 [[x, y], ...]
 * @param {Array<Object>} componentData.originPerforationPointList - 穿孔元数据（含 geometryConfig）
 * @param {Array<Object>} componentData.baseHoleInfoList - 底座孔信息
 * @param {Array<Object>} componentData.originBaseHoleInfoList - 底座孔元数据（含 geometryConfig）
 * @returns {Object} acrylic 对象，含 edgeHoles/slotRect/innerHoles/slotHoles
 *
 * @example
 * var acrylic = convertComponentDataToAcrylic(layerData.componentData);
 * // acrylic = {
 * //   edgeHoles: [[0.5, 0.08, 1.25, 3]],
 * //   slotRect: [0.35, 0.85, 0.1, 0.024, 10, 2.4, 0.5],
 * //   innerHoles: [[0.5, 0.5, 2.5]],
 * //   slotHoles: [[0.5, 0.5, 0.785, 12, 2.4]]
 * // }
 */
export function convertComponentDataToAcrylic(componentData) {
  if (!componentData) return {};

  var acrylic = {};

  // 钥匙扣 → edgeHoles: [[x, y, innerR, outerR], ...]  x,y 比例(0~1), innerR/outerR mm
  var origins;
  if (componentData.keyPointList && componentData.keyPointList.length) {
    origins = componentData.originCenterPointList || [];
    acrylic.edgeHoles = componentData.keyPointList.map(function (pt, idx) {
      var origin = origins[idx];
      var gc = origin && origin.geometryConfig;
      var innerR = gc ? gc.innerHoleDiameterMm / 2 : 0;
      var outerR = gc ? gc.outerHoleDiameterMm / 2 : 0;
      return [pt[0], pt[1], innerR, outerR];
    });
  }

  // 立牌 → slotRect: [x, y, widthRatio, heightRatio, slotWidth, slotHeight]
  // x/y/width/height 来自 rectCanvasToSlotRect（比例），slotWidth/slotHeight 来自 geometryConfig（mm）
  if (componentData.socketInfoList && componentData.socketInfoList.length) {
    var first = componentData.socketInfoList[0];
    if (first.slotRect) {
      var originSocket = (componentData.originSocketInfoList || [])[0];
      var gc = originSocket && originSocket.geometryConfig;
      var slotW = gc ? gc.slotWidthMm : 0;
      var slotH = gc ? gc.slotHeightMm : 0;
      acrylic.slotRect = [
        first.slotRect[0],
        first.slotRect[1],
        first.slotRect[2],
        first.slotRect[3],
        slotW,
        slotH
      ];
    }
  }

  // 穿孔 → innerHoles: [[x, y, r], ...]  x,y 比例(0~1), r mm
  if (
    componentData.perforationPointList &&
    componentData.perforationPointList.length
  ) {
    origins = componentData.originPerforationPointList || [];
    acrylic.innerHoles = componentData.perforationPointList.map(function (
      pt,
      idx
    ) {
      var origin = origins[idx];
      var r =
        origin && origin.geometryConfig ? origin.geometryConfig.radiusMm : 0;
      return [pt[0], pt[1], r];
    });
  }

  // 底座孔 → slotHoles: [[x, y, angle, slotWidth, slotHeight], ...]
  // thickness 字段废弃
  if (componentData.baseHoleInfoList && componentData.baseHoleInfoList.length) {
    origins = componentData.originBaseHoleInfoList || [];
    acrylic.slotHoles = componentData.baseHoleInfoList.map(function (
      info,
      idx
    ) {
      var origin = origins[idx];
      // slotWidth/slotHeight 来自 STAND_HOLE attr 的 width/height 字段（mm），对应 geometryConfig
      var slotWidth =
        origin && origin.geometryConfig ? origin.geometryConfig.widthMm : 0;
      var slotHeight =
        origin && origin.geometryConfig ? origin.geometryConfig.heightMm : 0;
      return [
        info.centerPoint.x,
        info.centerPoint.y,
        info.rotationRad,
        slotWidth,
        slotHeight
      ];
    });
  }

  return acrylic;
}
